import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { isMobilePhone } from './utils/device';

import AppShell from './AppShell';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Catalogue from './pages/Catalogue';
import Suppliers from './pages/Suppliers';
import SuppliersList from './pages/SuppliersList';
import SupplierProducts from './pages/SupplierProducts';
import NotFound from './pages/NotFound';
import EmaSupps from './pages/EmaSupps';
import DutchSupps from './pages/DutchSupps';
import AnsmSupps from './pages/AnsmSupps';
import HmaSupps from './pages/HmaSupps';
import ListHistory from './pages/ListHistory';
import MarketSignals from './pages/MarketSignals';
import Opportunities from './pages/Opportunities';
import BusinessCase from './pages/BusinessCase';
import BCEditor from './pages/BCEditor';
import Pipeline from './pages/Pipeline';
import Upload from './pages/Upload';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import ChatHistory from './pages/ChatHistory';
import ActivityLog from './pages/ActivityLog';
import DeliveryDelays from './pages/DeliveryDelays';
import FileMonitoring from './pages/FileMonitoring';
import ReportIncident from './pages/ReportIncident';
import TherapeuticAreaDevelopments from './pages/TherapeuticAreaDevelopments';
import MoleculeDevelopments from './pages/MoleculeDevelopments';
import PharmaceuticalFormDevelopments from './pages/PharmaceuticalFormDevelopments';
import SupplierPortfolioEvolution from './pages/SupplierPortfolioEvolution';
import PredictiveSupplierDevelopments from './pages/PredictiveSupplierDevelopments';
import NLMarketGapAnalysis from './pages/NLMarketGapAnalysis';
import MyFavoriteSuppliers from './pages/MyFavoriteSuppliers';
import SupplierContracts from './pages/SupplierContracts';
import MachData from './pages/MachData';
import RetailHospYear from './pages/RetailHospYear';
import SwitchList from './pages/SwitchList';
import CbgPage from './pages/CbgPage';
import FkPage from './pages/FkPage';
import ComingSoon from './pages/ComingSoon';
import SupplierCockpit from './pages/SupplierCockpit';
import MoleculeCockpit from './pages/MoleculeCockpit';
import NovaMobile from './pages/NovaMobile';

import { UploadTrackingProvider } from './context/UploadTrackingContext';
import ErrorBoundary from './components/shared/ErrorBoundary';

import './styles/theme.css';
import './components/layout/Layout.css';
import './pages/Login.css';

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  // MSAL redirect in progress — show loading, do NOT render routes
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '100vh', background: 'var(--surf)' }}>
        <div className="text-center" style={{ color: 'var(--t3)' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>✦</div>
          <div style={{ fontSize: 13 }}>Authenticating...</div>
        </div>
      </div>
    );
  }

  // Not authenticated — show Login page directly, no redirect
  if (!isAuthenticated) {
    return <Login />;
  }

  // On a mobile phone, the whole platform collapses to the /nova-mobile upload tool
  // (any domain, any path). Tablets and computers get the full app unchanged.
  const phone = isMobilePhone();

  // Authenticated — show the full app with routing
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <UploadTrackingProvider>
      {phone ? (
      <Routes>
        <Route path="/nova-mobile" element={<NovaMobile />} />
        <Route path="*" element={<Navigate to="/nova-mobile" replace />} />
      </Routes>
      ) : (
      <Routes>
        {/* Standalone mobile-only page — no sidebar/AppShell, still behind MSAL auth */}
        <Route path="nova-mobile" element={<NovaMobile />} />
        <Route element={<AppShell />}>
          <Route index                element={<Dashboard />} />
          <Route path="catalogue"     element={<Catalogue />} />
          <Route path="suppliers"     element={<Suppliers />} />
          <Route path="suppliers-list" element={<SuppliersList />} />
          <Route path="supplier-products" element={<SupplierProducts />} />
          <Route path="supplier-contracts" element={<SupplierContracts />} />
          <Route path="delivery-delays" element={<DeliveryDelays />} />
          <Route path="file-monitoring" element={<FileMonitoring />} />
          <Route path="ema-supps"        element={<EmaSupps />} />
          <Route path="dutch-supps"      element={<DutchSupps />} />
          <Route path="ansm-supps"       element={<AnsmSupps />} />
          <Route path="hma-supps"        element={<HmaSupps />} />
          <Route path="history"       element={<ListHistory />} />
          <Route path="signals"       element={<MarketSignals />} />
          <Route path="opportunities" element={<Opportunities />} />
          <Route path="bc"            element={<BusinessCase />} />
          <Route path="bc/editor"     element={<BCEditor />} />
          <Route path="pipeline"      element={<Pipeline />} />
          <Route path="upload"        element={<Upload />} />
          <Route path="settings"      element={<Settings />} />
          <Route path="profile"       element={<Profile />} />
          <Route path="chat-history"  element={<ChatHistory />} />
          <Route path="activity-log"  element={<ActivityLog />} />
          <Route path="therapeutic-area-developments" element={<TherapeuticAreaDevelopments />} />
          <Route path="molecule-developments" element={<MoleculeDevelopments />} />
          <Route path="pharmaceutical-form-developments" element={<PharmaceuticalFormDevelopments />} />
          <Route path="supplier-portfolio-evolution" element={<SupplierPortfolioEvolution />} />
          <Route path="predictive-supplier-developments" element={<PredictiveSupplierDevelopments />} />
          <Route path="nl-market-gap-analysis" element={<NLMarketGapAnalysis />} />
          <Route path="my-favorite-suppliers" element={<MyFavoriteSuppliers />} />
          <Route path="market-intelligence/mach" element={<MachData />} />
          <Route path="market-intelligence/retail-hosp/:year" element={<RetailHospYear />} />
          <Route path="cockpit/supplier/:name" element={<SupplierCockpit />} />
          <Route path="cockpit/molecule/:name" element={<MoleculeCockpit />} />
          <Route path="market-intelligence/red-list" element={<SwitchList listType="red" />} />
          <Route path="market-intelligence/orange-list" element={<SwitchList listType="orange" />} />
          <Route path="market-intelligence/cbg" element={<CbgPage />} />
          <Route path="market-intelligence/nhg" element={<ComingSoon title="NHG" />} />
          <Route path="market-intelligence/fk" element={<FkPage />} />
          <Route path="support/report-incident" element={<ReportIncident />} />
          <Route path="*"             element={<NotFound />} />
        </Route>
      </Routes>
      )}
      </UploadTrackingProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
